import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { engineers, type Engineer } from '@/mocks/talentPool';
import RequestMatchModal from './RequestMatchModal';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface RoleConfig {
  id: string;
  label: string;
  icon: string;
  skills: string[];
  description: string;
}

const roles: RoleConfig[] = [
  {
    id: 'frontend',
    label: 'Frontend Engineers',
    icon: 'ri-layout-masonry-line',
    skills: ['React', 'Vue.js', 'TypeScript', 'Next.js', 'GraphQL'],
    description: 'UI/UX implementation, component libraries, state management',
  },
  {
    id: 'backend',
    label: 'Backend Engineers',
    icon: 'ri-server-line',
    skills: ['Node.js', 'Python', 'Java', '.NET'],
    description: 'APIs, microservices, business logic, data layers',
  },
  {
    id: 'devops',
    label: 'DevOps Engineers',
    icon: 'ri-cloud-line',
    skills: ['AWS', 'Azure', 'Kubernetes', 'Docker', 'Terraform', 'CI/CD'],
    description: 'Infrastructure, deployment pipelines, monitoring',
  },
  {
    id: 'data',
    label: 'Data Engineers',
    icon: 'ri-database-2-line',
    skills: ['PostgreSQL', 'MongoDB', 'Spark', 'Snowflake', 'Kafka'],
    description: 'Data pipelines, warehousing, ETL, analytics',
  },
  {
    id: 'ai',
    label: 'AI/ML Engineers',
    icon: 'ri-brain-line',
    skills: ['PyTorch', 'TensorFlow'],
    description: 'Model training, inference pipelines, MLOps',
  },
  {
    id: 'qa',
    label: 'QA Engineers',
    icon: 'ri-bug-line',
    skills: ['Cypress', 'Playwright'],
    description: 'Test automation, regression suites, quality gates',
  },
  {
    id: 'mobile',
    label: 'Mobile Engineers',
    icon: 'ri-smartphone-line',
    skills: ['Swift'],
    description: 'iOS/Android apps, cross-platform development',
  },
];

const allSkills = roles.flatMap((r) => r.skills);

interface TeamMember {
  engineer: Engineer;
  assignedRole: string;
  newSkillsCovered: string[];
}

function getEngineerPrimaryRole(engineer: Engineer): string {
  let bestRole = 'fullstack';
  let bestCount = 0;
  for (const role of roles) {
    const count = engineer.skills.filter((s) => role.skills.includes(s)).length;
    if (count > bestCount) {
      bestCount = count;
      bestRole = role.id;
    }
  }
  return bestRole;
}

function buildTeam(
  selectedSkills: string[],
  roleCounts: Record<string, number>
): { team: TeamMember[]; uncovered: string[]; totalRateMin: number; totalRateMax: number } {
  const team: TeamMember[] = [];
  const usedIds = new Set<number>();
  let uncovered = [...selectedSkills];

  const roleOrder = Object.entries(roleCounts)
    .filter(([, count]) => count > 0)
    .sort((a, b) => {
      // prioritize roles whose primary skills have more uncovered entries
      const aUncovered = roles.find((r) => r.id === a[0])?.skills.filter((s) => uncovered.includes(s)).length || 0;
      const bUncovered = roles.find((r) => r.id === b[0])?.skills.filter((s) => uncovered.includes(s)).length || 0;
      return bUncovered - aUncovered;
    })
    .map(([roleId]) => roleId);

  for (const roleId of roleOrder) {
    const count = roleCounts[roleId];
    const roleSkills = roles.find((r) => r.id === roleId)?.skills || [];

    for (let i = 0; i < count; i++) {
      const candidates = engineers
        .filter((e) => !usedIds.has(e.id))
        .map((e) => {
          const primaryRole = getEngineerPrimaryRole(e);
          const roleBonus = primaryRole === roleId ? 10 : 0;
          const newCovered = e.skills.filter(
            (s) => uncovered.includes(s) && selectedSkills.includes(s)
          );
          const coverageScore = newCovered.length * 100 + roleBonus;
          const overallMatch = e.skills.filter((s) => selectedSkills.includes(s)).length;
          return { engineer: e, newCovered, coverageScore, overallMatch, primaryRole };
        })
        .sort((a, b) => {
          if (b.coverageScore !== a.coverageScore) return b.coverageScore - a.coverageScore;
          return b.overallMatch - a.overallMatch;
        });

      const pick = candidates[0];
      if (!pick) break;

      usedIds.add(pick.engineer.id);
      uncovered = uncovered.filter((s) => !pick.newCovered.includes(s));

      team.push({
        engineer: pick.engineer,
        assignedRole: roleId,
        newSkillsCovered: pick.newCovered,
      });
    }
  }

  const totalRateMin = team.reduce((sum, m) => sum + m.engineer.rateMin, 0);
  const totalRateMax = team.reduce((sum, m) => sum + m.engineer.rateMax, 0);

  return { team, uncovered, totalRateMin, totalRateMax };
}

const suggestedConfigs = [
  { label: 'MVP Squad', counts: { frontend: 1, backend: 1, devops: 0, data: 0, ai: 0, qa: 0, mobile: 0 }, skills: ['React', 'Node.js', 'TypeScript'] },
  { label: 'Full-Stack Team', counts: { frontend: 2, backend: 2, devops: 1, data: 1, ai: 0, qa: 1, mobile: 0 }, skills: ['React', 'Node.js', 'TypeScript', 'PostgreSQL', 'AWS', 'Docker', 'CI/CD'] },
  { label: 'AI Platform Team', counts: { frontend: 1, backend: 2, devops: 1, data: 2, ai: 2, qa: 1, mobile: 0 }, skills: ['Python', 'PyTorch', 'Spark', 'Kafka', 'React', 'Node.js', 'AWS'] },
  { label: 'Mobile Product Team', counts: { frontend: 1, backend: 2, devops: 1, data: 1, ai: 0, qa: 1, mobile: 2 }, skills: ['Swift', 'React', 'Node.js', 'Firebase', 'PostgreSQL'] },
];

export default function TeamBuilder() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState('frontend');
  const [roleCounts, setRoleCounts] = useState<Record<string, number>>({
    frontend: 0,
    backend: 0,
    devops: 0,
    data: 0,
    ai: 0,
    qa: 0,
    mobile: 0,
  });
  const [showResults, setShowResults] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState<Engineer | null>(null);

  const openModal = (engineer: Engineer) => {
    setSelectedEngineer(engineer);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => setSelectedEngineer(null), 300);
  };

  const toggleSkill = (skill: string) => {
    setSelectedSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
    setShowResults(false);
  };

  const adjustCount = (roleId: string, delta: number) => {
    setRoleCounts((prev) => ({
      ...prev,
      [roleId]: Math.max(0, Math.min(5, (prev[roleId] || 0) + delta)),
    }));
    setShowResults(false);
  };

  const totalHeadcount = Object.values(roleCounts).reduce((a, b) => a + b, 0);

  const applyPreset = (preset: (typeof suggestedConfigs)[0]) => {
    setSelectedSkills(preset.skills);
    setRoleCounts(preset.counts);
    setShowResults(false);
  };

  const currentCategorySkills =
    roles.find((r) => r.id === activeCategory)?.skills || [];

  const { team, uncovered, totalRateMin, totalRateMax } = useMemo(() => {
    if (!showResults || selectedSkills.length === 0 || totalHeadcount === 0) {
      return { team: [], uncovered: [], totalRateMin: 0, totalRateMax: 0 };
    }
    return buildTeam(selectedSkills, roleCounts);
  }, [showResults, selectedSkills, roleCounts, totalHeadcount]);

  const coveredSkills = selectedSkills.filter((s) => !uncovered.includes(s));
  const coveragePercent = selectedSkills.length > 0
    ? Math.round((coveredSkills.length / selectedSkills.length) * 100)
    : 0;

  const handleBuildTeam = () => {
    if (selectedSkills.length === 0 || totalHeadcount === 0) return;
    setIsBuilding(true);
    setTimeout(() => {
      setIsBuilding(false);
      setShowResults(true);
      const el = document.getElementById('team-results');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 900);
  };

  const teamByRole = useMemo(() => {
    const grouped: Record<string, TeamMember[]> = {};
    for (const member of team) {
      if (!grouped[member.assignedRole]) grouped[member.assignedRole] = [];
      grouped[member.assignedRole].push(member);
    }
    return grouped;
  }, [team]);

  return (
    <section
      id="team-builder"
      className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-team-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Smart Team Assembly</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Team
            <br />
            <span className="text-primary-600">Builder</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Define your tech stack and team size. Our optimizer assembles the ideal squad to cover every skill with zero gaps.
          </motion.p>
        </motion.div>

        {/* Presets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mb-8 md:mb-10"
        >
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center mb-3">
            Start with a pre-built configuration
          </p>
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {suggestedConfigs.map((preset) => (
              <button
                key={preset.label}
                onClick={() => applyPreset(preset)}
                className="px-4 py-2 bg-white border border-gray-200 rounded-full text-xs md:text-sm font-medium text-gray-700 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700 transition-all cursor-pointer whitespace-nowrap shadow-sm"
              >
                <i className="ri-group-line mr-1.5 text-gray-400"></i>
                {preset.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Stack Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="bg-white border border-gray-100 rounded-2xl md:rounded-3xl p-6 md:p-8 mb-6 md:mb-8 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 flex items-center justify-center bg-primary-50 rounded-lg">
              <i className="ri-stack-line text-primary-600 text-sm"></i>
            </div>
            <h3 className="text-base md:text-lg font-bold text-navy-900">1. Select Your Tech Stack</h3>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {roles.map((cat) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    isActive
                      ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                      : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.label.replace(' Engineers', '')}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2">
            <AnimatePresence mode="popLayout">
              {currentCategorySkills.map((skill) => {
                const isSelected = selectedSkills.includes(skill);
                return (
                  <motion.button
                    key={skill}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={() => toggleSkill(skill)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                      isSelected
                        ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:text-primary-600'
                    }`}
                  >
                    {isSelected && <i className="ri-check-line mr-1.5 text-xs"></i>}
                    {skill}
                  </motion.button>
                );
              })}
            </AnimatePresence>
          </div>

          {selectedSkills.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              <span className="text-xs text-gray-400 mr-1">Selected:</span>
              {selectedSkills.map((skill) => (
                <span
                  key={skill}
                  className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-primary-50 border border-primary-100 rounded-md text-xs font-medium text-primary-700"
                >
                  {skill}
                  <button
                    onClick={() => toggleSkill(skill)}
                    className="w-3.5 h-3.5 flex items-center justify-center hover:text-primary-900 cursor-pointer"
                  >
                    <i className="ri-close-line text-[10px]"></i>
                  </button>
                </span>
              ))}
            </div>
          )}
        </motion.div>

        {/* Role Counts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="bg-white border border-gray-100 rounded-2xl md:rounded-3xl p-6 md:p-8 mb-6 md:mb-8 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 flex items-center justify-center bg-navy-50 rounded-lg">
              <i className="ri-user-add-line text-navy-700 text-sm"></i>
            </div>
            <h3 className="text-base md:text-lg font-bold text-navy-900">2. Define Team Composition</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {roles.map((role) => {
              const count = roleCounts[role.id] || 0;
              const isActive = count > 0;
              return (
                <motion.div
                  key={role.id}
                  layout
                  className={`border rounded-xl p-4 md:p-5 transition-all duration-200 ${
                    isActive
                      ? 'border-primary-300 bg-primary-50/30'
                      : 'border-gray-100 bg-white hover:border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${
                      isActive ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'
                    } transition-colors`}>
                      <i className={`${role.icon} text-base`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-navy-900">
                        {role.label}
                      </p>
                      <p className="text-[11px] text-gray-500 leading-tight">
                        {role.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => adjustCount(role.id, -1)}
                        disabled={count === 0}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <motion.span
                        key={count}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        className="w-10 text-center text-lg font-bold text-navy-900"
                      >
                        {count}
                      </motion.span>
                      <button
                        onClick={() => adjustCount(role.id, 1)}
                        disabled={count >= 5}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                    {count > 0 && (
                      <motion.span
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md"
                      >
                        {count} slot{count > 1 ? 's' : ''}
                      </motion.span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {totalHeadcount > 0 && (
            <div className="mt-4 flex items-center gap-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-navy-900 rounded-lg">
                  <span className="text-white text-xs font-bold">{totalHeadcount}</span>
                </div>
                <span className="text-sm font-medium text-navy-900">Total headcount</span>
              </div>
              <div className="h-4 w-px bg-gray-200"></div>
              <span className="text-xs text-gray-500">
                {selectedSkills.length} skill{selectedSkills.length !== 1 ? 's' : ''} selected
              </span>
            </div>
          )}
        </motion.div>

        {/* Build Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.45, duration: 0.5 }}
          className="text-center mb-6 md:mb-8"
        >
          <button
            onClick={handleBuildTeam}
            disabled={selectedSkills.length === 0 || totalHeadcount === 0 || isBuilding}
            className={`px-10 py-4 rounded-xl text-base font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap group inline-flex items-center gap-2 ${
              selectedSkills.length > 0 && totalHeadcount > 0 && !isBuilding
                ? 'bg-navy-900 text-white hover:shadow-xl hover:shadow-navy-900/20 hover:-translate-y-0.5'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isBuilding ? (
              <>
                <i className="ri-loader-4-line animate-spin text-lg"></i>
                Optimizing...
              </>
            ) : (
              <>
                <i className="ri-magic-line text-lg"></i>
                Build Optimal Team
              </>
            )}
          </button>

          {selectedSkills.length === 0 || totalHeadcount === 0 ? (
            <p className="text-xs text-gray-400 mt-3">
              Select at least one skill and assign at least one role to build a team
            </p>
          ) : null}
        </motion.div>

        {/* Results */}
        <AnimatePresence>
          {showResults && team.length > 0 && (
            <motion.div
              id="team-results"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ...springTransition }}
            >
              {/* Coverage Summary Bar */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 md:p-6 mb-6 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-navy-900 mb-1">Team Coverage Analysis</h3>
                    <p className="text-sm text-gray-500">
                      {team.length} engineers covering {coveredSkills.length} of {selectedSkills.length} required skills
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Blended hourly rate</p>
                      <p className="text-xl font-bold text-navy-900">
                        ${totalRateMin}–${totalRateMax}
                        <span className="text-xs font-normal text-gray-400"> /hr combined</span>
                      </p>
                    </div>
                    <div className="w-16 h-16 relative flex items-center justify-center">
                      <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#f3f4f6" strokeWidth="3"></circle>
                        <circle
                          cx="18"
                          cy="18"
                          r="15.9155"
                          fill="none"
                          stroke="#0f766e"
                          strokeWidth="3"
                          strokeDasharray={`${coveragePercent}, 100`}
                          strokeLinecap="round"
                          className="transition-all duration-1000"
                        ></circle>
                      </svg>
                      <span className="absolute text-sm font-bold text-navy-900">
                        {coveragePercent}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Skill Coverage Grid */}
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {selectedSkills.map((skill) => {
                    const isCovered = coveredSkills.includes(skill);
                    const coverer = team.find((m) =>
                      m.engineer.skills.includes(skill)
                    )?.engineer.name;
                    return (
                      <div
                        key={skill}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs md:text-sm font-medium ${
                          isCovered
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                            : 'bg-red-50 border-red-200 text-red-700'
                        }`}
                      >
                        <i
                          className={`${
                            isCovered ? 'ri-checkbox-circle-line text-emerald-500' : 'ri-close-circle-line text-red-400'
                          }`}
                        ></i>
                        <div className="min-w-0">
                          <span className="truncate">{skill}</span>
                          {isCovered && coverer && (
                            <p className="text-[10px] text-emerald-600 truncate">
                              {coverer}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {uncovered.length > 0 && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl">
                    <p className="text-xs text-red-700">
                      <i className="ri-error-warning-line mr-1"></i>
                      <strong>Gap detected:</strong> {uncovered.join(', ')} — no engineer in your team covers these skills. Consider adding a specialist or adjusting your stack.
                    </p>
                  </div>
                )}
              </div>

              {/* Team Roster */}
              <h3 className="text-lg md:text-xl font-bold text-navy-900 mb-4 md:mb-5">
                Suggested Team
              </h3>

              {Object.entries(teamByRole).map(([roleId, members]) => {
                const roleLabel = roles.find((r) => r.id === roleId)?.label || roleId;
                return (
                  <div key={roleId} className="mb-6 md:mb-8">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-primary-50 rounded-md">
                        <i className="ri-team-line text-primary-600 text-xs"></i>
                      </div>
                      <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider">
                        {roleLabel}
                      </h4>
                      <span className="text-xs text-gray-400">({members.length})</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                      <AnimatePresence mode="popLayout">
                        {members.map((member, index) => {
                          const { engineer, newSkillsCovered } = member;
                          const allMatched = engineer.skills.filter((s) =>
                            selectedSkills.includes(s)
                          );
                          return (
                            <motion.div
                              key={engineer.id}
                              layout
                              initial={{ opacity: 0, y: 20, scale: 0.96 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              transition={{
                                delay: index * 0.08,
                                duration: 0.4,
                                ...springTransition,
                              }}
                              className="bg-white border border-gray-100 rounded-2xl p-5 hover:border-primary-200 hover:shadow-md transition-all duration-300"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-10 h-10 rounded-xl ${engineer.avatarColor} flex items-center justify-center flex-shrink-0`}
                                  >
                                    <span className="text-white text-xs font-bold">
                                      {engineer.initials}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="text-sm font-bold text-navy-900">
                                      {engineer.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {engineer.role}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-bold text-primary-600">
                                    ${engineer.rateMin}–${engineer.rateMax}
                                  </p>
                                  <p className="text-[10px] text-gray-400">/hr</p>
                                </div>
                              </div>

                              {newSkillsCovered.length > 0 && (
                                <div className="mb-3">
                                  <div className="flex items-center gap-1.5 mb-1.5">
                                    <i className="ri-shield-star-line text-emerald-500 text-xs"></i>
                                    <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">
                                      Key Contribution
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {newSkillsCovered.map((skill) => (
                                      <span
                                        key={skill}
                                        className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded text-[10px] md:text-xs font-medium text-emerald-700"
                                      >
                                        <i className="ri-check-line mr-0.5 text-[10px]"></i>
                                        {skill}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="mb-3">
                                <div className="flex flex-wrap gap-1">
                                  {allMatched.map((skill) => (
                                    <span
                                      key={skill}
                                      className={`px-2 py-0.5 rounded text-[10px] md:text-xs font-medium border ${
                                        newSkillsCovered.includes(skill)
                                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                          : 'bg-gray-50 border-gray-100 text-gray-500'
                                      }`}
                                    >
                                      {skill}
                                    </span>
                                  ))}
                                  {engineer.skills
                                    .filter((s) => !selectedSkills.includes(s))
                                    .map((skill) => (
                                      <span
                                        key={skill}
                                        className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-[10px] md:text-xs font-medium text-gray-400"
                                      >
                                        {skill}
                                      </span>
                                    ))}
                                </div>
                              </div>

                              <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
                                <span className="flex items-center gap-1">
                                  <i className="ri-time-line"></i>
                                  {engineer.years}yr
                                </span>
                                <span className="text-gray-300">·</span>
                                <span className="flex items-center gap-1">
                                  <i className="ri-map-pin-time-line"></i>
                                  {engineer.timezoneOffset}
                                </span>
                              </div>

                              <button
                                onClick={() => openModal(engineer)}
                                className="w-full py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap"
                              >
                                Request Match
                              </button>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  </div>
                );
              })}

              {/* Bottom CTA */}
              <div className="mt-8 md:mt-10 bg-navy-900 rounded-2xl p-6 md:p-8 text-center">
                <h4 className="text-lg font-bold text-white mb-2">
                  Ready to hire this team?
                </h4>
                <p className="text-sm text-gray-300 mb-5">
                  {team.length} engineers · ${totalRateMin}–${totalRateMax}/hr combined · {coveragePercent}% skill coverage
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    onClick={() => {
                      const el = document.getElementById('cta');
                      if (el) {
                        const offset = 80;
                        const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
                        window.scrollTo({ top: pos, behavior: 'smooth' });
                      }
                    }}
                    className="px-8 py-3.5 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group"
                  >
                    Request This Team
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
                  </button>
                  <button
                    onClick={() => {
                      setShowResults(false);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="px-6 py-3 border border-white/20 text-white text-sm font-medium rounded-xl hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Adjust Configuration
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Request Match Modal */}
        <RequestMatchModal
          engineer={selectedEngineer}
          isOpen={modalOpen}
          onClose={closeModal}
        />
      </div>
    </section>
  );
}